import { useState, useEffect, useRef } from 'react';

/**
 * 鍵盤可見性偵測 Hook
 * 綜合視窗高度變化、元素焦點判斷鍵盤強度
 */
interface WindowDimensions {
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  visualViewportHeight: number; // 必填，避免型別不安全
}

interface KeyboardState {
  isVisible: boolean;
  height: number;
}

const useKeyboardVisibility = (): boolean => {
  const [keyboardState, setKeyboardState] = useState<KeyboardState>({
    isVisible: false,
    height: 0,
  });
  // 初始視窗參數，visualViewportHeight 預設為可視區域高度或 innerHeight
  const initialDimensions = useRef<WindowDimensions>({
    width: 0,
    height: 0,
    orientation: 'portrait',
    visualViewportHeight:
      typeof window !== 'undefined' ? window.visualViewport?.height ?? window.innerHeight : 0,
  });
  const lastHeight = useRef<number>(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusedElement = useRef<Element | null>(null);

  useEffect(() => {
    // 取得當前畫面方向，避免鍵盤推起時誤判
    const getOrientation = (): 'portrait' | 'landscape' => {
      try {
        const currentWidth = window.innerWidth;
        const currentHeight = window.innerHeight;
        return currentWidth > currentHeight ? 'landscape' : 'portrait';
      } catch {
        return 'portrait'; // 安全回退值
      }
    };

    // 計算鍵盤偵測閾值，避免小幅高度變化誤判
    const getHeightThreshold = (): number => {
      return Math.max(100, window.innerHeight * 0.15);
    };

    // 檢查是否為輸入元素
    const isInputElement = (element: Element | null): boolean => {
      return element?.tagName === 'INPUT' || element?.tagName === 'TEXTAREA';
    };

    // 是否有輸入元素獲得焦點
    const hasInputFocus = (): boolean => {
      return isInputElement(document.activeElement);
    };

    // 初始化窗口尺寸
    const initDimensions = () => {
      initialDimensions.current = {
        width: window.innerWidth,
        height: window.innerHeight,
        orientation: getOrientation(),
        visualViewportHeight: window.visualViewport?.height || window.innerHeight,
      };
      lastHeight.current = window.innerHeight;
    };

    // 防抖動函數
    const debounce = (fn: () => void, delay: number) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(fn, delay);
    };

    // 視窗變化處理
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      const currentOrientation = getOrientation();
      const visualViewportHeight = window.visualViewport?.height || currentHeight;

      // 設備旋轉處理
      if (currentOrientation !== initialDimensions.current.orientation) {
        initialDimensions.current = {
          width: currentWidth,
          height: currentHeight,
          orientation: currentOrientation,
          visualViewportHeight,
        };
        setKeyboardState({ isVisible: false, height: 0 });
        console.log('旋轉螢幕');
        return;
      }

      // 進行鍵盤顯示判斷（防抖動，避免頻繁觸發）
      /*
        鍵盤顯示判斷邏輯簡述：
        1. 以可視高度差（VisualViewport 或 window.innerHeight）為主，判斷鍵盤是否彈出。
        2. 若高度差大於閾值，且有輸入框聚焦或高度突變（如鍵盤彈出），則視為鍵盤顯示。
        3. 可有效排除旋轉螢幕、系統 UI 變化等誤判。
      */
      debounce(() => {
        const heightThreshold = getHeightThreshold();
        let heightDifference = 0;
        let suddenHeightChange = false;
        let isKeyboardVisible = false;
        const inputFocus = hasInputFocus();

        if (window.visualViewport) {
          const baseViewportHeight =
            initialDimensions.current.visualViewportHeight ?? visualViewportHeight;
          heightDifference = baseViewportHeight - visualViewportHeight;
          suddenHeightChange = heightDifference > heightThreshold * 1.5;
        } else {
          heightDifference = initialDimensions.current.height - currentHeight;
          suddenHeightChange = Math.abs(lastHeight.current - currentHeight) > heightThreshold;
          lastHeight.current = currentHeight;
        }

        isKeyboardVisible =
          heightDifference > heightThreshold && (inputFocus || suddenHeightChange);

        setKeyboardState({
          isVisible: isKeyboardVisible,
          height: isKeyboardVisible ? heightDifference : 0,
        });
      }, 100);
    };

    // 處理元素獲得焦點事件，若為輸入元件則延遲觸發 resize
    const handleFocus = (e: FocusEvent) => {
      focusedElement.current = e.target as Element;
      if (isInputElement(focusedElement.current)) {
        setTimeout(handleResize, 300);
      }
    };

    // 處理元素失去焦點事件，若無任何輸入元件聚焦則關閉鍵盤狀態
    const handleBlur = () => {
      focusedElement.current = null;
      setTimeout(() => {
        if (!hasInputFocus()) {
          setKeyboardState({ isVisible: false, height: 0 });
        }
      }, 100);
    };

    // 事件監聽器參數結構
    interface EventListenerConfig {
      target: EventTarget; // 目標對象（如 window 或 document）
      type: string; // 事件類型（如 focus、resize）
      listener: EventListenerOrEventListenerObject; // 對應處理函式
    }

    // 批次註冊事件監聽器，並回傳自動解綁函式
    const registerEventListeners = (configs: EventListenerConfig[]): (() => void) => {
      configs.forEach(({ target, type, listener }) => {
        target.addEventListener(type, listener);
      });
      return () => {
        configs.forEach(({ target, type, listener }) => {
          target.removeEventListener(type, listener);
        });
      };
    };

    // 初始化視窗尺寸與狀態
    initDimensions();

    // 註冊各類事件監聽，確保鍵盤偵測邏輯能即時反應
    const listeners: EventListenerConfig[] = [
      { target: window, type: 'resize', listener: handleResize },
      { target: window, type: 'focus', listener: handleResize },
      { target: document, type: 'focusin', listener: handleFocus },
      { target: document, type: 'focusout', listener: handleBlur },
    ];

    if (window.visualViewport) {
      listeners.push({
        target: window.visualViewport,
        type: 'resize',
        listener: handleResize,
      });
    }

    // 初始檢查與清理設置
    handleResize();
    const cleanup = registerEventListeners(listeners);

    return () => {
      cleanup();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return keyboardState.isVisible;
};

export default useKeyboardVisibility;
